import { AuthService } from '../../auth/auth.service';
import {
    InteractionEntity,
    InteractionEntityType,
    NoteEntity,
    NoteEntityType,
} from '../../coaching-base/config';
import { DeliveryType } from '../../coaching-base/general';
import { epicDependencies } from '../../store';
import { createReducer } from '../../store.model';

import {
    ActionType,
    RootDeleteAction,
    RootDeleteResultAction,
    RootDismissMessageAction,
    RootNavigateAction,
    RootReloadAction,
    RootReloadResultAction,
    RootSaveAction,
    RootSaveResultAction,
    RootSendNotificationAction,
    RootSendNotificationResultAction,
    RootSetModeAction,
} from './observation.action';
import { observationFormValueTransformer } from './observation.form';
import { ObservationMode } from './observation.model';
import { defaultState, RootState } from './observation.state';
import {
    overrideObservation,
    touchOverriddenObservation,
} from './override-observation.function';

const { builder, reducer } = createReducer<ActionType, RootState>(
    defaultState(),
);

builder
    .add<RootReloadAction>(
        ActionType.COACHING_OBSERVATION__ROOT__RELOAD,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                },
                justSaved: false,
                willNavigate: false,
            };
            return allowedActionsReducer(nextState);
        },
    )
    .add<RootReloadResultAction>(
        ActionType.COACHING_OBSERVATION__ROOT__RELOAD_RESULT,
        (state, action) => {
            const behaviours = action.payload.behaviours || state.behaviours;
            const subBehaviours =
                action.payload.subBehaviours || state.subBehaviours;
            const metrics = action.payload.metrics || state.metrics;

            const noteEntity = action.payload.coachingEvent.childEntities.find(
                (e) => e.entityType === NoteEntityType,
            ) as NoteEntity;
            const interactionEntity =
                action.payload.coachingEvent.childEntities.find(
                    (e) => e.entityType === InteractionEntityType,
                ) as InteractionEntity;

            const interactionTypeId =
                interactionEntity.interactionTypes &&
                interactionEntity.interactionTypes.length !== 0
                    ? interactionEntity.interactionTypes[0].interactionTypeId
                    : null;

            let nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                },
                behaviours,
                subBehaviours,
                metrics,
                queues: action.payload.queues,
                coachingEvent: action.payload.coachingEvent,
                noteEntity,
                interactionEntity,
                defaultInteractionTypeId: interactionTypeId,
                nextCommitmentDraftId: undefined,
                draftIdByIdentifier: {},
                nextCommitmentId: undefined,
            };

            overrideObservation(
                {
                    event: action.payload.event,
                    eventParticipants: [],
                    focusContainers: action.payload.focusContainers,
                },
                action.payload.extraOptions?.observation,
            );

            nextState = reloadAndSaveReducer(nextState, action.payload);

            touchOverriddenObservation(
                nextState.initialFormValue,
                action.payload.extraOptions?.observation,
            );

            return allowedActionsReducer(nextState);
        },
    );

builder
    .add<RootSaveAction>(
        ActionType.COACHING_OBSERVATION__ROOT__SAVE,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    save: true,
                },
                justSaved: false,
            };
            return allowedActionsReducer(nextState);
        },
    )
    .add<RootSaveResultAction>(
        ActionType.COACHING_OBSERVATION__ROOT__SAVE_RESULT,
        (state, action) => {
            let nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    save: false,
                },
                justSaved: true,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                willNavigate:
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    action.payload.actions &&
                    action.payload.actions.some(
                        (e) =>
                            e.type ===
                            ActionType.COACHING_OBSERVATION__ROOT__NAVIGATE,
                    ),
                nextCommitmentDraftId: action.payload.nextCommitmentDraftId,
                draftIdByIdentifier: action.payload.draftIdByIdentifier || {},
                nextCommitmentId: action.payload.nextCommitmentId,
            };
            nextState = reloadAndSaveReducer(nextState, action.payload);
            return allowedActionsReducer(nextState);
        },
    );

builder
    .add<RootSendNotificationAction>(
        ActionType.COACHING_OBSERVATION__ROOT__SEND_NOTIFICATION,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    notification: true,
                },
            };
            return allowedActionsReducer(nextState);
        },
    )
    .add<RootSendNotificationResultAction>(
        ActionType.COACHING_OBSERVATION__ROOT__SEND_NOTIFICATION_RESULT,
        (state, action) => {
            const nextState: RootState = {
                ...state,
                pending: {
                    ...state.pending,
                    notification: false,
                },
            };
            return allowedActionsReducer(nextState);
        },
    );

builder.add<RootNavigateAction>(
    ActionType.COACHING_OBSERVATION__ROOT__NAVIGATE,
    (state, action) => {
        return {
            ...state,
            willNavigate: action.payload.scenario != null,
            navigationScenario: action.payload.scenario,
            navigationScenarioOpts: action.payload.opts
                ? { ...action.payload.opts }
                : {},
        };
    },
);

builder.add<RootDismissMessageAction>(
    ActionType.COACHING_OBSERVATION__ROOT__DISMISS_MESSAGE,
    (state, action) => {
        return {
            ...state,
            justSaved: false,
        };
    },
);

builder
    .add<RootDeleteAction>(
        ActionType.COACHING_OBSERVATION__ROOT__DELETE,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    delete: true,
                },
            };
        },
    )
    .add<RootDeleteResultAction>(
        ActionType.COACHING_OBSERVATION__ROOT__DELETE_RESULT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    delete: false,
                },
                event: {
                    ...state.event,
                    deleted: action.payload.success
                        ? true
                        : state.event.deleted,
                },
            };
        },
    );

builder.add<RootSetModeAction>(
    ActionType.COACHING_OBSERVATION__ROOT__SET_MODE,
    (state, action) => {
        const nextState: RootState = {
            ...state,
            mode: action.payload.mode,
        };
        return allowedActionsReducer(nextState);
    },
);

// TODO move?
function reloadAndSaveReducer(
    state: RootState,
    action: RootReloadResultAction | RootSaveResultAction,
): RootState {
    return {
        ...state,
        event: action.event,
        performer: action.performer,
        observer: action.observer,
        focusContainers: action.focusContainers,
        initialFormValue: observationFormValueTransformer(
            action.event,
            action.focusContainers,
        ),
    };
}

// TODO move
function allowedActionsReducer(state: RootState): RootState {
    const service = epicDependencies.injector.get<AuthService>(AuthService); // TODO FIXME. this can be done better. just to get current user id

    let currentUserIsObserver: boolean;
    let observerIsPerformer: boolean;
    const pending = Object.values(state.pending).some((e) => !!e);

    try {
        currentUserIsObserver = service.user.id === state.observer.userId;
        observerIsPerformer = state.observer.userId === state.performer.userId;
    } catch (e) {
        return {
            ...state,
            allowedActions: {
                ...state.allowedActions,
                cancel:
                    state.mode === ObservationMode.DEFAULT ||
                    state.mode === ObservationMode.POP_UP ||
                    state.mode === ObservationMode.REVIEW_EDIT,
                share: false,
                edit: false,
                delete: false,
                publish: false,
                save: false,

                addNote: false,
                addInteraction: false,

                displayNotes: false,
                displayInteractions: false,
            },
            enabledActions: {
                ...state.enabledActions,
                cancel: !pending && state.mode === ObservationMode.REVIEW_EDIT,
                share: false,
                edit: false,
                delete: false,
                publish: false,
                save: false,
            },
        };
    }

    return {
        ...state,
        allowedActions: {
            ...state.allowedActions,

            cancel:
                state.mode === ObservationMode.DEFAULT ||
                state.mode === ObservationMode.POP_UP ||
                state.mode === ObservationMode.REVIEW_EDIT,

            share:
                state.event.published &&
                (currentUserIsObserver ||
                    (observerIsPerformer &&
                        state.event.deliveryTypeId === DeliveryType.SELF)),
            /* && !state.event.shareWithPerformer */ edit:
                !state.event.published &&
                (state.mode === ObservationMode.REVIEW_READ_ONLY ||
                    state.mode === ObservationMode.REVIEW_EDIT),

            delete: state.event.eventId != null && !state.event.published,

            publish: !state.event.published,

            save:
                !state.event.published &&
                (state.mode === ObservationMode.DEFAULT ||
                    state.mode === ObservationMode.POP_UP ||
                    state.mode === ObservationMode.REVIEW_EDIT),

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            addNote: state.noteEntity && state.noteEntity.enabled,

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            addInteraction:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                state.interactionEntity &&
                state.interactionEntity.enabled &&
                !state.event.published,

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            displayNotes: state.noteEntity && state.noteEntity.enabled,

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            displayInteractions:
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                state.interactionEntity && state.interactionEntity.enabled,
        },
        enabledActions: {
            ...state.enabledActions,
            cancel:
                !pending &&
                (state.mode === ObservationMode.DEFAULT ||
                    state.mode === ObservationMode.POP_UP ||
                    state.mode === ObservationMode.REVIEW_EDIT),

            share: !pending,

            edit: !pending && state.mode === ObservationMode.REVIEW_READ_ONLY,

            delete: !pending,

            publish: !pending,

            save:
                !pending &&
                (state.mode === ObservationMode.DEFAULT ||
                    state.mode === ObservationMode.POP_UP ||
                    state.mode === ObservationMode.REVIEW_EDIT),
        },
    };
}

export default {
    coachingObservation: {
        root: reducer,
    },
};
